import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueSafeTeleport from 'vue-safe-teleport'
import { Quasar, Notify, Dialog, LocalStorage } from 'quasar'

import App from '@/App.vue'
import auth0Plugin from '@/setups/authSetup'
import router from './router'

import * as Sentry from '@sentry/vue'

import '@quasar/extras/material-icons/material-icons.css'
import '@quasar/extras/material-icons-outlined/material-icons-outlined.css'
import 'quasar/src/css/index.sass'
import axios, { type AxiosRequestConfig } from 'axios'
import tracker, { tracker as $tracker } from './tracker'
import LogRocket from 'logrocket'

import {
  Chart,
  LinearScale,
  Legend,
  PointElement,
  TimeScale,
  LineElement,
  BarElement,
  CategoryScale,
  Tooltip,
  Filler
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'
import { PiniaColada } from '@pinia/colada'

Chart.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
  annotationPlugin
)

// vue-chartjs defers chart.update() to nextTick after a data change. If the chart unmounts in
// the same tick (e.g. a `v-if="isLoading"` swap when a query key changes), the update runs on a
// destroyed chart and throws "Cannot read properties of null (reading 'ownerDocument')".
// A destroyed chart has a null canvas and nothing to draw, so skip the update.
const chartUpdate = Chart.prototype.update
Chart.prototype.update = function (this: Chart, ...args: Parameters<Chart['update']>) {
  if (!this.canvas) {
    return
  }
  return chartUpdate.apply(this, args)
}

const commit = {
  hash: import.meta.env.VITE_COMMIT_HASH || 'local-dev',
  timestamp: import.meta.env.VITE_COMMIT_TIMESTAMP || 'no-commit-timestamp'
}

console.log('Environment:', import.meta.env.MODE)
console.log({
  'Commit Hash:': commit.hash,
  'Commit Timestamp:': commit.timestamp
})
$tracker.version(commit.hash, commit.timestamp)

// A tab left open across a deploy still points at the old chunk hashes, which are gone
// from the server, so lazy routes fail with "Unable to preload CSS" (FRONTEND-JC).
// Reload once to pick up the new build. If we already reloaded in the last minute,
// the chunk is really missing, so let the error through instead of looping.
const preloadReloadKey = 'vite-preload-reload-at'
window.addEventListener('vite:preloadError', (event) => {
  try {
    const lastReload = Number(sessionStorage.getItem(preloadReloadKey))
    if (lastReload && Date.now() - lastReload < 60_000) {
      return
    }
    sessionStorage.setItem(preloadReloadKey, String(Date.now()))
  } catch {
    // No sessionStorage (private mode, blocked storage), so we can't guard against a loop
    return
  }
  event.preventDefault()
  location.reload()
})

// vue-router's isNavigationFailure() relies on `instanceof Error`, so duck-type instead
const isNavigationFailureLike = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as { type?: unknown }).type === 'number' &&
  'to' in error &&
  'from' in error

const app = createApp(App)

const setupApp = () => {
  if (import.meta.env.MODE !== 'reports') {
    app.use(auth0Plugin)
  }

  app.use(VueSafeTeleport)
  app.use(router)
  app.use(createPinia())
  app.use(PiniaColada, {
    queryOptions: {
      refetchOnMount: false, // We don't want to refetch on mount because we want to use the cached data
      refetchOnReconnect: false, // We don't want to refetch on reconnect because we want to use the cached data
      refetchOnWindowFocus: false // We don't want to refetch on window focus because we want to use the cached data
    }
  })
  app.use(tracker)

  if (import.meta.env.MODE === 'production' && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration({ router })],
      // Tracing. This was the only project still at 100%, which made it a
      // disproportionate share of our span bill. Errors are unaffected.
      tracesSampleRate: 0.08,
      // Browser resource-timing and long-animation-frame spans are ~51% of this
      // project's span volume and we never look at them.
      ignoreSpans: [{ op: /^resource\./ }, { op: 'ui.long-animation-frame' }],
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [/^https:\/\/api.hellodata\.ai/],
      release: 'frontend@' + commit.hash,
      environment: import.meta.env.MODE,
      ignoreErrors: [
        // Thrown by injected third-party code (browser extensions) that reads
        // responseText on every XHR load, including our blob downloads. Axios
        // never reads responseText for blob responses, so this is never ours.
        /Failed to read the 'responseText' property from 'XMLHttpRequest'.*\(was 'blob'\)/,
        // Thrown inside LogRocket's recorder when other injected code (browser
        // extensions) has already made adoptedStyleSheets non-configurable. Only
        // that session's stylesheet capture is lost, and we can't undo the lock.
        /Cannot redefine property: adoptedStyleSheets/,
        // Thrown by a headless scanner (GCP, Council Bluffs) that patches Error so
        // new instances are non-extensible. vue-router then can't set `type` on its
        // redirect error. Real browsers never hit this.
        /Cannot add property type, object is not extensible/
      ],
      beforeSend: (event, hint) => {
        // Guard redirects (next({ path: ... })) are control flow, not errors. vue-router
        // handles them itself, but some crawlers inject a script that wraps the global Error
        // constructor, which breaks the router's `instanceof Error` check and sends the
        // redirect to router.onError, where Sentry reports it (FRONTEND-HY).
        if (isNavigationFailureLike(hint.originalException)) {
          return null
        }
        try {
          if (LogRocket) {
            Sentry.setContext('logrocket', {
              url: LogRocket.sessionURL
            })
          }
        } catch (error) {
          // TODO: remove the try/catch once we're sure the whole timing works well
          console.error('Error setting LogRocket context:', error)
        }
        return event
      }
    })
  }

  const preferDarkMode = LocalStorage.getItem<boolean | string>('prefer-dark')
  let dark = 'auto' as 'auto' | boolean
  if (preferDarkMode !== null) {
    dark = preferDarkMode === true || preferDarkMode === 'true'
  }

  app.use(Quasar, {
    plugins: { Notify, Dialog, LocalStorage },
    config: {
      dark
    }
  })

  app.mount('#app')
}

setupApp()

// add an interceptor to axios to detect 429 errors (rate limiting)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.error('Received a 401 Unauthorized error')
      await auth0Plugin.loginWithRedirect()
    } else if (error.response?.status === 403) {
      console.error('Received a 403 Forbidden error')
      Notify.create({
        message: 'You do not have permission to access this resource',
        type: 'negative'
      })
      await auth0Plugin.loginWithRedirect()
    } else if (error.response?.status === 429) {
      Notify.create({
        message: 'Too many requests, please try again in a minute',
        type: 'negative'
      })
    } else if (error.response?.status === 409) {
      return Promise.reject(
        Object.assign(
          new Error('Request was interrupted. A newer request replaced the previous one.'),
          { status: error.response.status }
        )
      )
    } else if (error.response?.status === 500) {
      Notify.create({
        message:
          'An error occurred, please try again later, if the problem persists, please contact support',
        type: 'negative'
      })
    } else if (error.response?.status === 422) {
      // Doing this to not encapsulate the error in another error object.
      // This allows us to access the original error directly in the business logic.
      if (error instanceof Error) {
        return Promise.reject(error)
      }
    }
    // Keep HTTP status on the rejected Error. If this interceptor ever wraps the
    // generated client's axios instance (e.g. axios is deduped), classifiers like
    // isPropertyNotFoundError still see 404 vs a retryable failure.
    return Promise.reject(
      Object.assign(new Error(error.response?.data?.message || 'Unknown error'), {
        status: error.response?.status
      })
    )
  }
)

// Makes a request to the host to get the current version hash. If the version
// available is different from the current version that's running, then a toast
// will appear letting the user know that there is a new version available.
const checkForNewVersion = async function () {
  const config: AxiosRequestConfig = {
    headers: {
      Accept: 'text/plain'
    },
    responseType: 'text'
  }
  const response = await axios.get<string>('/version', config)

  // If the request fails, we can't determine if the versions are different.
  if (response.status !== 200) {
    return
  }

  // Trim to remove EOF and other white space characters from the response.
  const version = response.data.trim()

  if (commit.hash !== version) {
    $tracker.newVersionNotificationShown()
    Notify.create({
      message: 'A new version of Hello Data is available!',
      color: 'primary',
      actions: [
        {
          icon: 'refresh',
          color: 'white',
          noCaps: true,
          handler: () => {
            location.reload()
          }
        }
      ]
    })
  }
}

const twentyFourHours = 1000 * 60 * 60 * 24
setInterval(() => {
  void checkForNewVersion()
}, twentyFourHours)
